import utils from '../utils/utils'
import { animate, cubicBezier } from 'animejs';
import globalVar from '../utils/globalVar'
import hammer from '../libs/hammer.js';

class Module09Slider {
  constructor() {
    let cache = {};
    let initFunc = () => {
      initCache();
      registerEvents();
    }

    let initCache = () => {
      cache.module = document.querySelectorAll('.module-09-slider .slider_container');
    };

    let registerEvents = () => {
      cache.module.forEach(slider => {
        const module       = slider.closest('.module-09-slider');
        const arrowWrapper = module.querySelector('.arrow_wrapper');
        const isMobileMode = arrowWrapper && arrowWrapper.classList.contains('mobile');

        const arrowRight = module.querySelector('.arrow_right');
        const arrowLeft  = module.querySelector('.arrow_left');
        if (arrowRight) arrowRight.addEventListener('click', () => slideRight(slider));
        if (arrowLeft)  arrowLeft.addEventListener('click',  () => slideLeft(slider));

        if (isMobileMode) {
          let destroyScroll = null;
          let mc = null;

          const initForBreakpoint = () => {
            if (!globalVar.tablet.matches) {
              mc?.destroy(); mc = null;
              destroyScroll = initScrollSlider(slider);
            } else {
              destroyScroll?.(); destroyScroll = null;
              mc = new hammer(slider);
              mc.get('swipe').set({ direction: hammer.DIRECTION_HORIZONTAL });
              mc.on('swipeleft',  () => slideRight(slider));
              mc.on('swiperight', () => slideLeft(slider));
            }
          };

          initForBreakpoint();
          globalVar.tablet.addEventListener('change', initForBreakpoint);
        } else {
          const mc = new hammer(slider);
          mc.get('swipe').set({ direction: hammer.DIRECTION_HORIZONTAL });
          mc.on('swipeleft',  () => slideRight(slider));
          mc.on('swiperight', () => slideLeft(slider));
        }
      });
    };

    let initScrollSlider = (slider) => {
      const module = slider.closest('.module-09-slider');
      const slides = slider.querySelectorAll('.slide');
      const count  = slides.length;
      if (count <= 1) return () => {};

      let isAnimating = false;

      // 1 viewport per slide + 1 for the sticky element itself.
      const setHeight = () => {
        module.style.height = (count * window.innerHeight * 0.5 + window.innerHeight) + 'px';
      };
      setHeight();

      // Show only the target slide, reset transforms.
      const resetSlides = (idx) => {
        slides.forEach((s, i) => {
          s.style.display   = i === idx ? 'block' : 'none';
          s.style.transform = '';
          const content = s.querySelector('.content');
          if (content) { content.style.opacity = i === idx ? '1' : '0'; content.style.transform = ''; }
          const bg = s.querySelector('.bg_media');
          if (bg) bg.style.transform = '';
        });
        slider.setAttribute('data-index', idx);
      };
      resetSlides(0);

      // Compute which slide index the current scroll position maps to.
      const getTargetIndex = () => {
        const sectionTop = module.getBoundingClientRect().top + window.scrollY;
        const progress   = Math.max(0, window.scrollY - sectionTop);
        return Math.min(count - 1, Math.floor(progress / (window.innerHeight * 0.5)));
      };

      const onScroll = () => {
        if (isAnimating) return;

        // If Lenis is doing a programmatic scroll (anchor nav) targeting outside
        // this section, let it pass through without locking.
        if (lenis && lenis.targetScroll !== undefined) {
          const sectionTop    = module.getBoundingClientRect().top + window.scrollY;
          const sectionBottom = sectionTop + module.offsetHeight;
          if (lenis.targetScroll < sectionTop || lenis.targetScroll > sectionBottom) return;
        }

        const target  = getTargetIndex();
        const current = parseInt(slider.getAttribute('data-index'));
        if (target === current) return;

        isAnimating = true;
        window.__smoothScroll?.lock();
        if (target > current) {
          slideRight(slider, () => { isAnimating = false; window.__smoothScroll?.unlock(); });
        } else {
          slideLeft(slider, () => { isAnimating = false; window.__smoothScroll?.unlock(); });
        }
      };

      // Prefer Lenis scroll event for sub-frame accuracy; fall back to native.
      const lenis = window.__smoothScroll?.lenis;
      const onLenisScroll = () => onScroll();
      if (lenis) {
        lenis.on('scroll', onLenisScroll);
      } else {
        window.addEventListener('scroll', onScroll, { passive: true });
      }

      // Keep section height in sync with viewport on resize.
      window.addEventListener('resize', setHeight);

      return () => {
        if (lenis) lenis.off('scroll', onLenisScroll);
        else window.removeEventListener('scroll', onScroll);
        window.removeEventListener('resize', setHeight);
        window.__smoothScroll?.unlock();
        module.style.height = '';
        isAnimating = false;
        slider.setAttribute('data-index', 0);
      };
    };

    let slideRight = (slider, onDone) => {
      const isMobile = globalVar.tablet.matches;
      const dur = isMobile ? 0.5 : 1;
      let index = parseInt(slider.getAttribute('data-index'));
      let slides = slider.querySelectorAll('.slide');
      const length = slides.length;
      const initSlide = slides[index];

      if (index < length - 1) {
        index++;
      } else {
        index = 0;
      }
      slider.setAttribute('data-index', index);
      const targetSlide = slides[index];


      animate(initSlide, {
        x: ["0%", "-100%"],
        duration: 1800 * dur,
        ease: cubicBezier(0.567, -0.013,0.493,0.98),
        onComplete: function() {
          if(initSlide.querySelector('video')) {
            initSlide.querySelector('video').pause();
          }
          initSlide.style.display = 'none';
        }
      });
      if(initSlide.querySelector('.content')) {
        const initContent = initSlide.querySelector('.content');
        animate(initContent, {
            opacity: [1, 0],
            scale: [1, 0.95],
            duration: 600 * dur,
            ease: "outSine",
        });
      }

      if(targetSlide){
        targetSlide.style.display = 'block';
        if(targetSlide.querySelector('video')) {
          targetSlide.querySelector('video').play();
        }
        animate(targetSlide, {
          x: ["100%", "0%"],
          duration: 1800 * dur,
          ease: cubicBezier(0.567, -0.013,0.493,0.98),
        });
        animate(targetSlide.querySelector('.bg_media'), {
          scale: [1.2, 1],
          delay: 1100 * dur,
          duration: 1000 * dur,
          ease: cubicBezier(0.567, -0.013,0.493,0.98),
          onComplete: function() {
            const targetContent = targetSlide.querySelector('.content');
            animate(targetContent, {
              opacity: [0, 1],
              scale: [0.95, 1],
              duration: 600 * dur,
              ease: "outSine",
              onComplete: function() { onDone?.(); }
            });
          }
        });
      }
    };

    let slideLeft = (slider, onDone) => {
      const isMobile = globalVar.tablet.matches;
      const dur = isMobile ? 0.5 : 1;
      let index = parseInt(slider.getAttribute('data-index'));
      let slides = slider.querySelectorAll('.slide');
      const length = slides.length;
      const initSlide = slides[index];

      if (index > 0) {
        index--;
      } else {
        index = length - 1;
      }
      slider.setAttribute('data-index', index);
      const targetSlide = slides[index];


      animate(initSlide, {
        x: ["0%", "100%"],
        duration: 1800 * dur,
        ease: cubicBezier(0.567, -0.013,0.493,0.98),
        onComplete: function() {
          if(initSlide.querySelector('video')) {
            initSlide.querySelector('video').pause();
          }
          initSlide.style.display = 'none';
        }
      });
      if(initSlide.querySelector('.content')) {
        const initContent = initSlide.querySelector('.content');
        animate(initContent, {
            opacity: [1, 0],
            scale: [1, 0.95],
            duration: 600 * dur,
            ease: "outSine",
        });
      }

      if(targetSlide){
        targetSlide.style.display = 'block';
        if(targetSlide.querySelector('video')) {
          targetSlide.querySelector('video').play();
        }
        animate(targetSlide, {
          x: ["-100%", "0%"],
          duration: 1800 * dur,
          ease: cubicBezier(0.567, -0.013,0.493,0.98),
        });
        animate(targetSlide.querySelector('.bg_media'), {
          scale: [1.2, 1],
          delay: 1100 * dur,
          duration: 1000 * dur,
          ease: cubicBezier(0.567, -0.013,0.493,0.98),
          onComplete: function() {
            const targetContent = targetSlide.querySelector('.content');
            animate(targetContent, {
              opacity: [0, 1],
              scale: [0.95, 1],
              duration: 600 * dur,
              ease: "outSine",
              onComplete: function() { onDone?.(); }
            });
          }
        });
      }
    };

    initFunc();
  };
}
export { Module09Slider }
